import { useEffect, useState } from 'react'
import { isInsideTaskDetails } from '../../../utils/taskDetailsFocus'

/**
 * Meldet, ob die Leertaste gerade gehalten wird — der Griff, mit dem React
 * Flow im Maus-Werkzeug vorübergehend schiebt (`panActivationKeyCode`).
 *
 * Anders als der Zeiger-Ankerpunkt ein State und kein Ref: der Wert steuert
 * eine Klasse, soll also neu zeichnen. Er wechselt höchstens zweimal pro
 * Tastendruck.
 *
 * React Flow schiebt zwar von selbst, ändert dabei aber weder seine
 * `draggable`- noch seine `dragging`-Klasse — den Greif-Cursor muss der
 * Aufrufer daher selbst setzen.
 *
 * @example
 * const spaceHeld = useSpaceKeyHeld(isActiveTab && tool === 'select')
 */
export function useSpaceKeyHeld(enabled: boolean): boolean {
  const [held, setHeld] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setHeld(false)
      return
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      // In einer Notiz ist die Leertaste ein Leerzeichen; React Flow prüft
      // für sein eigenes Schieben dasselbe.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // In den **Aufgaben-Details** drückt die Leertaste den Knopf, auf dem der
      // Fokus steht — die Greifhand daneben wäre eine zweite Bedeutung für
      // denselben Anschlag (CONTEXT.md).
      if (isInsideTaskDetails(e.target)) return
      setHeld(true)
    }

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') setHeld(false)
    }

    // Wer mit gedrückter Leertaste das Fenster wechselt, bekommt nie ein
    // `keyup` — ohne diese beiden bliebe die Greifhand für immer stehen.
    const release = () => setHeld(false)

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)
    window.addEventListener('blur', release)
    document.addEventListener('visibilitychange', release)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      window.removeEventListener('blur', release)
      document.removeEventListener('visibilitychange', release)
      setHeld(false)
    }
  }, [enabled])

  return held
}
